import { Injectable } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/compat/database';


@Injectable({
    providedIn: 'root',
})
/**
 * Server time without a round trip.
 *
 * The SDK keeps the difference between the server clock and the client clock under the
 * local path `.info/serverTimeOffset` (taken from the connection handshake, held in
 * memory - reading it never touches the network). Subscribed once so that the query
 * stays synchronous: the auto-save that fires when the page is left must not have an
 * await before its write frame, otherwise the write is never formulated in the document
 * the browser is tearing down (workorder-server-time-offset-unload-save-2026-09-17).
 *
 * This replaces the old write-then-read of `whoo/currentTime`, which needed two round
 * trips. A plain number is written instead of `ServerValue.TIMESTAMP`, because that
 * sentinel fires the onHeaderChange function twice (local estimate, then the server
 * value) and would export two BigQuery rows per header change.
 */
export class ServerTimeService {
    private offsetMs = 0;
    private hasOffset = false;

    constructor(private afd: AngularFireDatabase) {
        // deliberately never unsubscribed: root service, lives as long as the app
        this.afd.database.ref('.info/serverTimeOffset').on('value', snapshot => {
            this.offsetMs = snapshot.val() ?? 0;
            this.hasOffset = snapshot.val() !== null;
        });
    }

    /** server clock minus client clock in ms, null before the SDK delivered the offset - diagnostics (skewed device clock) */
    public get clockSkewMs(): number | null {
        return this.hasOffset ? Math.round(this.offsetMs) : null;
    }

    /** server time in ms - synchronous, no round trip. Before the first connection the client clock (offset 0). */
    public now(): number {
        return Date.now() + this.offsetMs;
    }

    public getServerTime(): Date {
        return new Date(this.now());
    }
}
